import { useRef, useState } from "react";
import { Upload, Loader2, X, AlertCircle } from "lucide-react";
import {
  SIGNATURE_UPLOAD_ACCEPT,
  SIGNATURE_UPLOAD_MAX_BYTES,
} from "@shared/agreement";
import { processSignatureUpload, SignatureImageError } from "@/lib/signatureImage";

/**
 * Upload an image of a signature, as an alternative to drawing one.
 *
 * Everything happens in the browser: the file is decoded, its paper background
 * is made transparent, it is cropped to the ink and re-encoded as a PNG. The
 * result is byte-identical in kind to what the drawing pad produces, so the
 * server, the stored record and the stamped PDF need no special case.
 *
 * Re-encoding also strips EXIF, so a phone photo's GPS coordinates never leave
 * the device.
 *
 * The preview is shown on a CHEQUERED background rather than white, because a
 * white preview would hide exactly the failure this needs to surface: an image
 * whose background was not removed. On the chequerboard, leftover paper is
 * immediately obvious before the signer commits.
 */
export interface SignatureUploadProps {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  theme?: "dark" | "light";
}

export default function SignatureUpload({ value, onChange, disabled, theme = "dark" }: SignatureUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const dark = theme === "dark";
  const frame = dark ? "bg-white/5 border-white/15" : "bg-white border-gray-200";
  const frameActive = dark ? "border-[#84ab4a] bg-[#84ab4a]/10" : "border-[#84ab4a] bg-[#84ab4a]/5";
  const label = dark ? "text-white/40" : "text-gray-400";
  const strong = dark ? "text-white/80" : "text-gray-700";
  const btn = dark
    ? "text-white/50 hover:text-white/80 border-white/15 hover:border-white/30"
    : "text-gray-500 hover:text-gray-800 border-gray-200 hover:border-gray-300";

  const handleFile = async (file: File | undefined) => {
    if (!file || disabled) return;
    setError(null);

    if (file.size > SIGNATURE_UPLOAD_MAX_BYTES) {
      setError(`That file is ${(file.size / 1024 / 1024).toFixed(1)}MB. Please use one under ${SIGNATURE_UPLOAD_MAX_BYTES / 1024 / 1024}MB.`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file, such as a PNG or JPG.");
      return;
    }

    setBusy(true);
    try {
      const { dataUrl } = await processSignatureUpload(file);
      onChange(dataUrl);
    } catch (e) {
      onChange(null);
      setError(e instanceof SignatureImageError ? e.message : "That image could not be processed. Please try another.");
    } finally {
      setBusy(false);
      // Reset the input so re-picking the SAME file still fires a change event.
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => {
    onChange(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={SIGNATURE_UPLOAD_ACCEPT}
        className="hidden"
        disabled={disabled}
        onChange={e => handleFile(e.target.files?.[0])}
      />

      {value ? (
        <div className={`relative rounded-lg border overflow-hidden ${frame}`}>
          {/* Chequerboard, so any background the processing failed to remove
              is visible to the signer before they commit. */}
          <div
            className="flex items-center justify-center p-4"
            style={{
              minHeight: 150,
              backgroundImage:
                "linear-gradient(45deg, rgba(128,128,128,.18) 25%, transparent 25%, transparent 75%, rgba(128,128,128,.18) 75%)," +
                "linear-gradient(45deg, rgba(128,128,128,.18) 25%, transparent 25%, transparent 75%, rgba(128,128,128,.18) 75%)",
              backgroundSize: "16px 16px",
              backgroundPosition: "0 0, 8px 8px",
            }}
          >
            <img src={value} alt="Your uploaded signature" className="max-h-[130px] max-w-full object-contain" />
          </div>
          <button
            type="button"
            onClick={clear}
            disabled={disabled}
            aria-label="Remove uploaded signature"
            className="absolute top-2 right-2 p-1.5 rounded-md bg-black/50 text-white/80 hover:text-white hover:bg-black/70 transition-colors disabled:opacity-40"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
          className={`w-full rounded-lg border border-dashed transition-colors flex flex-col items-center justify-center gap-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed ${
            dragging ? frameActive : frame
          }`}
          style={{ minHeight: 150 }}
        >
          {busy ? (
            <>
              <Loader2 className={`w-5 h-5 animate-spin ${label}`} />
              <span className={`text-xs ${label}`}>Processing your signature…</span>
            </>
          ) : (
            <>
              <Upload className={`w-5 h-5 ${label}`} />
              <span className={`text-sm font-medium ${strong}`}>Choose an image of your signature</span>
              <span className={`text-[11px] ${label}`}>or drag one here. PNG or JPG.</span>
            </>
          )}
        </button>
      )}

      {error ? (
        <p className="text-xs text-red-400 mt-2 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span>{error}</span>
        </p>
      ) : (
        <div className="flex items-center justify-between mt-2">
          <span className={`text-[11px] ${label}`}>
            {value
              ? "Signature ready. The background has been removed."
              : "Sign on paper, photograph or scan it, then upload."}
          </span>
          {value && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
              className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors disabled:opacity-30 ${btn}`}
            >
              Replace
            </button>
          )}
        </div>
      )}
    </div>
  );
}
